import { computed, ref } from 'vue'
import { useNow } from '@vueuse/core'
import moment from 'moment-timezone'
import { CardReadService } from 'app-core'
import type { IWorkingDayCloseResult } from 'app-core'
import i18n from '@/i18n'
import { emitter } from '@/utilities/emitter'
import { useAppStore } from '@/stores/useAppStore'
import { useToast } from './useToast'
import { useConfirm } from './useConfirm'

const t = i18n.global.t

// One Kraj dana state for the whole signed-in session, not per view: the terminal's
// answer can arrive after the cashier left Working Day, and the navigation shows the
// reminder on every screen. Nothing here touches the store at import time — the
// router imports MainLayout (a consumer) before Pinia is installed.
const settling = ref(false)
const now = useNow({ interval: 60_000 })
const settlementOverdue = computed(() => CardReadService.isSettlementOverdue(moment(now.value)))

/** The terminal's answer to Kraj dana, or the provider giving up on one it never got. */
function onWorkingDayClosed(result: IWorkingDayCloseResult) {
  const { toast } = useToast()
  settling.value = false

  if (result.success) {
    CardReadService.recordSettlement()
    toast(t('general.end_of_day_success'), 'success')
  } else {
    toast(t(`order.error.end_of_day.${result.error}`), 'error')
  }
}

// App.vue forwards the terminal's workingDayClosed event here; this module loads with
// MainLayout, before any Kraj dana can start.
emitter.on('workingDayClosed', onWorkingDayClosed)

/** The navigation's reminder: Kraj dana is overdue on this device. */
export function useSettlementReminder() {
  return { settlementOverdue }
}

/**
 * "Kraj dana" (card-terminal end-of-day settlement) on the Working Day screen — its only
 * entry point; never offered from the card-sale or cancel-refund flow.
 */
export function useWorkingDayClose() {
  const store = useAppStore()
  const { toast } = useToast()
  const { confirmCloseWorkingDay } = useConfirm()

  const hasSettlement = computed(() => CardReadService.hasSettlement())
  const canStart = computed(() => store.isOnline && !settling.value && CardReadService.canCloseWorkingDay())
  const lastSettledAt = computed(() => store.cardSettlement.lastSettledAt)

  async function closeWorkingDay() {
    if (!canStart.value || !await confirmCloseWorkingDay()) return

    settling.value = true
    try {
      const result = await CardReadService.startCloseWorkingDay()
      if (!result.success) {
        settling.value = false
        toast(t(`order.error.end_of_day.${result.error}`), 'error')
      }
    } catch (error) {
      console.error('[CardPayment] Kraj dana could not be sent to the terminal:', error)
      settling.value = false
      toast(t('order.error.end_of_day.start_payment_failed'), 'error')
    }
  }

  return { hasSettlement, settlementOverdue, lastSettledAt, settling, canStart, closeWorkingDay }
}
